import {JourneyState} from "./journey";
import {DaysRange} from "../../components/activity/hotel/choose-hotel/utils";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../types-enums/activity";
import cloneDeep from "lodash/cloneDeep";
import {hasExactDates} from "../../utils/getter/journey";
import {createArrayFromNumber, getDaysCountBetweenDates} from "../../utils/common";
import {DurationProps} from "../../types-enums/activity-common-properties";
import {isActivityCarBusState, isActivityHotelState} from "../../utils/check/type-guards";
import {v4 as uuidv4} from "uuid";
import {ActivityHotelState} from "../../types-enums/activity-hotel";
import {
    removeReturnReference,
    setCo2KgEToOneDayEquivalent,
    updateAnyDistanceRelatedActivityProp
} from "../../utils/setter/activities";
import {ActivityCarBusState} from "../../types-enums/activity-car-bus";
import {getDurationInDaysOrNights} from "../../utils/getter/common";
import {setDurationOfHotelToOneWhenAddingToJourney} from "../../utils/converter/hotel";
import {setCarBusDurationToOneWhenAddingToJourney} from "../../utils/converter/car-bus";
import {getReturnActivity} from "../../utils/getter/activities";
import {activityHasReturnOption} from "../../utils/check/activities";

export const updateDateInJourneyDuration = (state: JourneyState, newDates: DaysRange) => {
    const {startDate, endDate} = newDates
    state.journeyDuration.dates = {startDate, endDate}
    state.journeyDuration.daysOrNights = null
}


/**
 * This function adds the day of a journey to the Activity and returns a deep copy of the updated Activity.
 */
export const addJourneyDayToReturnActivityOnAddToJourney = (
    activity: AllActivityTypes,
    activities: AllActivityTypesArray,
    day: number,
) => {
    if (activityHasReturnOption(activity)) {
        const returnRoute = getReturnActivity(activity, activities)
        if (returnRoute) returnRoute.common.day = day
        return returnRoute
    }
}

/**
 * Adds an Activity to the journey. Takes the duration of the Activity into consideration and adds for each day/night of
 * the Activity an Activity-Object to the Activities-Array with it's corresponding CO2 Kg E emissions.
 * It also handles Activities where the return route needs to be displayed separately (like flights)
 */
export const splitAndAddActivityToJourney = (
    activity: AllActivityTypes,
    day: number,
    journeyActivities: AllActivityTypesArray,
    travelComponentActivities: AllActivityTypesArray,
    journeyDuration: DurationProps
) => {
    const activityType = activity.common.activityType
    const updatedActivities = cloneDeep(journeyActivities)
    const travelComponentActivitiesForMutation = cloneDeep(travelComponentActivities)
    removeReturnReference(activity)
    updateAnyDistanceRelatedActivityProp(activity)
    setCo2KgEToOneDayEquivalent(activity, true)
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            activity.common.day = day
            const returnRoute = addJourneyDayToReturnActivityOnAddToJourney(activity, travelComponentActivitiesForMutation, day)
            updatedActivities.push(activity)
            if (returnRoute) updatedActivities.push(returnRoute)
            break;
        case ACTIVITY_TYPE.hotel:
            if (isActivityHotelState(activity)) {
                const {hotelStayDuration} = activity
                const hotelDurationInNights = getDurationInDaysOrNights(hotelStayDuration, true)
                if (hotelDurationInNights !== null) addActivityBasedOnDurationToJourney(
                    activity,
                    hotelDurationInNights,
                    updatedActivities,
                    day,
                    journeyDuration
                )
            }
            break;
        case ACTIVITY_TYPE.car_bus:
            if (isActivityCarBusState(activity)) {
                const carDurationInDays = activity.countDays
                addActivityBasedOnDurationToJourney(
                    activity,
                    carDurationInDays,
                    updatedActivities,
                    day,
                    journeyDuration
                )
            }
            break;
        default:
            activityType satisfies never;
    }
    return updatedActivities
}

/**
 * Activities that go over several days must be split into Activities with duration of one day/night. The calculated
 * co2 must be adjusted to this.
 */
const addActivityBasedOnDurationToJourney = (
    activity: ActivityHotelState | ActivityCarBusState,
    duration: number,
    updatedActivities: AllActivityTypesArray,
    day: number,
    journeyDuration: DurationProps
) => createArrayFromNumber(duration).forEach((_, index) => {
    let updatedActivity = cloneDeep(activity as ActivityHotelState)
    updatedActivity.common.day = day + index
    updatedActivity.common.id = uuidv4()
    setCo2KgEToOneDayEquivalent(cloneDeep(updatedActivity), false)
    if (isActivityCarBusState(updatedActivity)) setCarBusDurationToOneWhenAddingToJourney(updatedActivity)
    if (isActivityHotelState(updatedActivity)) setDurationOfHotelToOneWhenAddingToJourney(updatedActivity, journeyDuration)
    updatedActivities.push(updatedActivity)
})

/**
 * Updates either journeyDuration.dates or journeyDuration.days the journeyDuration either with the
 * new date range or the count of the days of the given date range.
 */
export const updateJourneyDurationWithDates = (
    currentJourneyDuration: DurationProps,
    newDuration: DaysRange,
    updatedJourneyDuration: DurationProps
) => {
    const {startDate, endDate} = newDuration
    const journeyHasExactDates = hasExactDates(currentJourneyDuration)
    if (journeyHasExactDates) {
        updatedJourneyDuration.dates!!.startDate = startDate
        updatedJourneyDuration.dates!!.endDate = endDate
    } else {
        const newJourneyDurationInDays = getDaysCountBetweenDates(startDate, endDate) + 1
        updatedJourneyDuration.daysOrNights = newJourneyDurationInDays
    }
}

export const updateJourneyDurationWithDays = (
    currentJourneyDuration: DurationProps,
    newDuration: number,
    updatedJourneyDuration: DurationProps
) => {
    const journeyHasExactDates = hasExactDates(currentJourneyDuration)
    if (!journeyHasExactDates && currentJourneyDuration.daysOrNights === null) {
        updatedJourneyDuration.daysOrNights = newDuration === 0 ? null : newDuration
    } else if (journeyHasExactDates) {
        const {dates} = currentJourneyDuration
        const {startDate} = dates!!
        if (updatedJourneyDuration.dates !== null) updatedJourneyDuration.dates.endDate = startDate.plus({days: newDuration - 1})
    } else if (currentJourneyDuration.daysOrNights !== null) {
        updatedJourneyDuration.daysOrNights = newDuration === 0 ? null : newDuration
    } else {

    }
}

/**
 * Returns the new journey duration. Independently of the type of newDuration, it checks if the journey has exact dates
 * and updates either journeyDuration.dates or journeyDuration.days with the new duration.
 */
export const getNewJourneyDuration = (currentJourneyDuration: DurationProps, newDuration: number | DaysRange) => {
    let updatedJourneyDuration = cloneDeep(currentJourneyDuration) as DurationProps
    if (typeof newDuration === 'number') {
        updateJourneyDurationWithDays(currentJourneyDuration, newDuration, updatedJourneyDuration)
    } else {
        updateJourneyDurationWithDates(currentJourneyDuration, newDuration, updatedJourneyDuration)
    }
    return updatedJourneyDuration
}

/**
 * Adds a day to the journey and makes sure, it does this either on journeyDuration.dates OR journeyDuration.days,
 * whether the journeyDuration as exact dates or not.
 */
export const addOneDayToJourney = (state: JourneyState) => {
    const {journeyDuration} = state
    const {daysOrNights} = journeyDuration
    const journeyHasExactDates = hasExactDates(journeyDuration)
    if (daysOrNights === null && !journeyHasExactDates) {
        state.journeyDuration.daysOrNights = 1
    } else if (state.journeyDuration.daysOrNights !== null) {
        state.journeyDuration.daysOrNights = state.journeyDuration.daysOrNights + 1
    } else if (journeyHasExactDates) {
        state.journeyDuration.dates!!.endDate = state.journeyDuration.dates!!.endDate.plus({days: 1})
    } else {

    }
}

/**
 * Moves activities that cannot be on the last day of the journey  (i.e. Hotels because they are overnight) one day up
 */
export const changeDayOfActivitiesOnLastDayOfJourney = (updatedActivities: AllActivityTypesArray, newJourneyDurationInDays: number) => updatedActivities.map(activity => {
    const {common: {day, activityType}} = activity
    if (day !== null) {
        switch (activityType) {
            case ACTIVITY_TYPE.flight:
                break;
            case ACTIVITY_TYPE.hotel:
                if (day === newJourneyDurationInDays) activity.common.day!! -= 1
                break;
            case ACTIVITY_TYPE.car_bus:
                break;
            default:
                activityType satisfies never;
        }
    }
    return activity
})